import { useState } from 'react'
import { useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { LookalikeSearchHandler } from '@/components/cross-camera/LookalikeSearchHandler/LookalikeSearchHandler'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useUnlinkCrossCameraTrack } from '@/queries/crossCameraQueries'
import { showToast } from '@/theme/notifications'
import { TrackedPerson } from '@/types/event'
import { CrudAction, Resource } from '@/types/permissions'
import { EventTrackedPeople } from './EventTrackedPeople'

type TrackedPeopleHandlerProps = {
  appId: string
  people: TrackedPerson[]
}

export const TrackedPeopleHandler = ({
  appId,
  people
}: TrackedPeopleHandlerProps) => {
  const intl = useIntl()
  const [lookalikesOf, setLookalikesOf] = useState<string | null>(null)
  const [canSplit] = useHasPermissions(Resource.Applications, [
    CrudAction.Update
  ])

  const {
    mutateAsync: unlinkTrack,
    isPending: isUnlinking,
    variables: unlinkVariables
  } = useUnlinkCrossCameraTrack()

  const handleNotSamePersonClick = async (trackingId: string) => {
    try {
      await unlinkTrack({ application_id: appId, tracking_id: trackingId })
      showToast(
        intl.formatMessage({ id: 'crossCamera.lookalikes.split' }),
        'success'
      )
    } catch (error) {
      showToast(
        extractErrorMessage(
          error,
          intl.formatMessage({ id: 'crossCamera.lookalikes.splitError' })
        ),
        'error'
      )
    }
  }

  return (
    <>
      <EventTrackedPeople
        people={people}
        canSplit={canSplit}
        splittingTrackingId={
          isUnlinking ? unlinkVariables?.tracking_id : undefined
        }
        onFindLookalikesClick={setLookalikesOf}
        onNotSamePersonClick={(trackingId) =>
          void handleNotSamePersonClick(trackingId)
        }
      />

      {lookalikesOf && (
        <LookalikeSearchHandler
          appId={appId}
          trackingId={lookalikesOf}
          onClose={() => setLookalikesOf(null)}
        />
      )}
    </>
  )
}
